<template>
  <div>
    <q-dialog v-model="passagesDialog">
      <q-card style="width: 700px; max-width: 90vw">
        <q-toolbar>
          <q-toolbar-title class="text-blue-brand text-bold"
            >Passages</q-toolbar-title
          >
          <span class="text-caption text-grey-7 q-mr-xs">Marker Name:</span
          ><span class="text-caption text-grey-9">{{
            selectedMarkerName
          }}</span>
          <q-space />
          <q-btn
            v-if="sortedMarkers.length > 1"
            flat
            round
            dense
            icon="chevron_left"
            color="blue-brand"
            :disable="loading"
            @click.stop="onPrevMarker"
          >
            <q-tooltip>Previous marker</q-tooltip>
          </q-btn>
          <q-btn
            v-if="sortedMarkers.length > 1"
            flat
            round
            dense
            icon="chevron_right"
            color="blue-brand"
            :disable="loading"
            @click.stop="onNextMarker"
          >
            <q-tooltip>Next marker</q-tooltip>
          </q-btn>
          <q-btn
            flat
            round
            dense
            icon="refresh"
            :loading="loading"
            @click="fetchPassages"
          />
          <q-btn
            flat
            round
            dense
            icon="filter_alt"
            :color="filterGreenMl ? 'green' : 'grey-6'"
            @click="filterGreenMl = !filterGreenMl"
          >
            <q-tooltip>{{
              filterGreenMl
                ? 'Showing green ML results only'
                : 'Filter to green ML results'
            }}</q-tooltip>
          </q-btn>
          <q-btn
            v-close-popup
            flat
            round
            dense
            icon="close"
            @click="closeViewPsgs"
          />
        </q-toolbar>

        <div v-if="errorMsg" class="q-pb-sm">
          <q-badge class="q-ml-sm" color="red">
            {{ errorMsg }}
          </q-badge>
        </div>

        <!-- EN-11: marker and UTC date/time filter (applied in the browser). -->
        <div
          class="row items-start q-gutter-sm q-px-sm q-pb-sm"
          data-testid="view-passages-filter"
        >
          <q-select
            v-if="markerFilterOptions.length > 0"
            :model-value="activePipelineSectionMarkerId"
            dense
            filled
            emit-value
            map-options
            :options="markerFilterOptions"
            :disable="loading"
            label="Marker"
            style="min-width: 160px"
            data-testid="view-passages-marker-filter"
            @update:model-value="onMarkerFilterSelected"
          />
          <q-input
            v-model="filterFrom"
            dense
            filled
            mask="####-##-## ##:##"
            label="From (UTC)"
            hint="YYYY-MM-DD HH:mm"
            style="width: 190px"
            data-testid="view-passages-from"
          >
            <template #append>
              <q-icon name="event" class="cursor-pointer">
                <q-popup-proxy transition-show="scale" transition-hide="scale">
                  <div class="row no-wrap">
                    <q-date v-model="filterFrom" mask="YYYY-MM-DD HH:mm" />
                    <q-time
                      v-model="filterFrom"
                      mask="YYYY-MM-DD HH:mm"
                      format24h
                    />
                  </div>
                </q-popup-proxy>
              </q-icon>
            </template>
          </q-input>
          <q-input
            v-model="filterTo"
            dense
            filled
            mask="####-##-## ##:##"
            label="To (UTC)"
            hint="YYYY-MM-DD HH:mm"
            style="width: 190px"
            data-testid="view-passages-to"
          >
            <template #append>
              <q-icon name="event" class="cursor-pointer">
                <q-popup-proxy transition-show="scale" transition-hide="scale">
                  <div class="row no-wrap">
                    <q-date v-model="filterTo" mask="YYYY-MM-DD HH:mm" />
                    <q-time
                      v-model="filterTo"
                      mask="YYYY-MM-DD HH:mm"
                      format24h
                    />
                  </div>
                </q-popup-proxy>
              </q-icon>
            </template>
          </q-input>
          <q-btn
            flat
            dense
            no-caps
            label="Clear"
            color="blue-brand"
            :disable="!filterFrom && !filterTo"
            data-testid="view-passages-clear"
            @click="clearTimeFilter"
          />
        </div>
        <div
          v-if="timeFilterRange.error"
          class="q-px-sm q-pb-sm text-negative text-caption"
          data-testid="view-passages-filter-error"
        >
          {{ timeFilterRange.error }}
        </div>

        <PassagesTable
          ref="passagesTable"
          v-model:pagination="passagePagination"
          :rows="displayedPassages"
          :loading="loading"
          :no-data-label="noDataLabel"
          dialog-source="marker_passages"
          ml-opens-chart
          :pig-run-id="activePigRunId"
          :pipeline-section-marker-id="activePipelineSectionMarkerId"
          :marker-name="selectedMarkerName"
          :active="passagesDialog && chartStepsHere"
        />
      </q-card>
    </q-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { EventBus } from '@/utils/EventBus';
import { IPassage } from '@/models/Passage';
import {
  PigRunMapEntry,
  PipelineSectionMarkerState,
  SelectedMarker,
} from '@/models/PigRunState';
import {
  WaveformTypeLabel,
  IPassageWaveformSummary,
} from '@/models/PassageWaveform';
import PigRunService from '@/services/PigRunService';
// EN-1851: the table, chart and verify actions are shared with the tracksheet.
import PassagesTable from '@/components/PassagesTable.vue';
import {
  getMLColor,
  getMLMean,
  getMLWaveforms,
  waveformMLColor,
} from '@/utils/PassageTableRows';
// EN-11: View Passages filter.
import {
  SelectOption,
  UtcRange,
  buildMarkerFilterOptions,
  buildUtcRange,
  filterRowsByUtcRange,
} from '@/utils/HistoryFilter';
import { respondsToSwitchPassage } from '@/utils/MarkerPassagesPopover';

/** A picker value counts once fully typed (YYYY-MM-DD HH:mm). */
const COMPLETE_FILTER_LENGTH = 16;

export default defineComponent({
  name: 'PipelineSectionMarkerPassagesDialog',
  components: { PassagesTable },
  // EN-1852: `marker-changed` fires when the user moves to another marker
  // (next, previous or the Marker select), with the same payload a map click
  // gives setSelectedMarker. The marker card listens so it follows the dialog.
  emits: ['marker-changed'],
  data() {
    return {
      passagesDialog: false,
      loading: false,
      selectedMarkerName: '',
      activePipelineSectionMarkerId: null as number | null,
      activePigRunId: null as number | null,
      passages: [] as IPassage[],
      errorMsg: '',
      passagePagination: { rowsPerPage: 10 },
      currentMarkerIndex: -1,
      sortedMarkers: [] as PipelineSectionMarkerState[],
      unsubscribeAction: null as (() => void) | null,
      refreshDebounceTimer: null as ReturnType<typeof setTimeout> | null,
      refreshMaxWaitTimer: null as ReturnType<typeof setTimeout> | null,
      filterGreenMl: false,
      // EN-11: UTC date/time filter, kept across SignalR refreshes and
      // marker navigation; cleared when the dialog is opened for a marker.
      filterFrom: '',
      filterTo: '',
    };
  },
  computed: {
    /** EN-1851: next / previous step here only when the chart opened here. */
    chartStepsHere(): boolean {
      return respondsToSwitchPassage(
        'marker_passages',
        this.$store.state.passage?.passageDialogSource
      );
    },
    timeFilterRange(): UtcRange {
      const complete = (v: string) =>
        v && v.length >= COMPLETE_FILTER_LENGTH ? v : '';
      return buildUtcRange(complete(this.filterFrom), complete(this.filterTo));
    },
    noDataLabel(): string {
      return this.passages.length > 0
        ? 'No passages match the filter'
        : 'No passages received';
    },
    markerFilterOptions(): SelectOption<number>[] {
      return buildMarkerFilterOptions(this.sortedMarkers);
    },
    displayedPassages(): IPassage[] {
      const inRange = filterRowsByUtcRange(
        this.passages,
        (row) => row.passageTimeStamp,
        this.timeFilterRange
      );
      if (!this.filterGreenMl) return inRange;
      return inRange.filter((row) =>
        this.getMLWaveforms(row).some((wf) =>
          this.wfColor(wf).includes('green')
        )
      );
    },
  },
  watch: {
    // EN-11: paging follows the filtered rows.
    timeFilterRange() {
      this.resetPassagePage();
    },
    filterGreenMl() {
      this.resetPassagePage();
    },
  },
  created() {
    this.unsubscribeAction = this.$store.subscribeAction((action: any) => {
      const refreshedPigRunId =
        typeof action.payload === 'number'
          ? action.payload
          : action.payload?.pigRunId;
      if (action.type === 'passageRecieved') {
        this.onSignalRPassageOrRunUpdate();
      }
      if (
        action.type === 'refreshSinglePigRun' &&
        this.activePigRunId != null &&
        refreshedPigRunId === this.activePigRunId
      ) {
        this.onSignalRPassageOrRunUpdate();
      }
      if (
        action.type === 'mlResultReceived' &&
        this.activePigRunId != null &&
        action.payload?.pigRunIds?.includes(this.activePigRunId)
      ) {
        this.onSignalRPassageOrRunUpdate();
      }
    });
  },
  mounted() {
    EventBus.on('passage-verified', this.handlePassageVerified as any);
  },
  beforeUnmount() {
    EventBus.off('passage-verified', this.handlePassageVerified as any);
    if (this.unsubscribeAction) {
      this.unsubscribeAction();
    }
    if (this.refreshDebounceTimer) {
      clearTimeout(this.refreshDebounceTimer);
    }
    if (this.refreshMaxWaitTimer) {
      clearTimeout(this.refreshMaxWaitTimer);
    }
  },
  methods: {
    async openNew(
      pipelineSectionMarkerId: number,
      markerName: string,
      pigRunId?: number
    ) {
      this.activePipelineSectionMarkerId = pipelineSectionMarkerId;
      this.selectedMarkerName = markerName;
      this.activePigRunId = pigRunId ?? null;
      this.sortedMarkers = [];
      this.currentMarkerIndex = -1;
      this.passages = [];
      this.errorMsg = '';
      // EN-1510: a verify left over from a previous open must not carry over.
      // ($refs may be absent when the method runs outside a mounted component,
      // e.g. in the EN-1852 navigation spec; the table may not be rendered yet.)
      (this.$refs?.passagesTable as any)?.resetPendingVerify?.();
      this.filterFrom = '';
      this.filterTo = '';
      this.resetPassagePage();
      this.passagesDialog = true;
      if (pigRunId) {
        await this.loadMarkers(pigRunId, pipelineSectionMarkerId);
      }
      await this.fetchPassages();
    },
    async loadMarkers(pigRunId: number, currentMarkerId: number) {
      // Try the active pig runs in the store first
      const entry = (
        this.$store.state.pigRun.pigRuns as Record<number, PigRunMapEntry>
      )[pigRunId];
      if (entry) {
        this.sortedMarkers = [...entry.markers].sort(
          (a, b) => a.sortOrder - b.sortOrder
        );
      } else {
        // Completed run — fetch markers from API
        try {
          const response = await PigRunService.getPigRun(pigRunId);
          const markers = response.data.markers || [];
          this.sortedMarkers = [...markers].sort(
            (a: any, b: any) => a.sortOrder - b.sortOrder
          );
        } catch (error) {
          console.error('Failed to load markers for pig run:', error);
        }
      }
      this.currentMarkerIndex = this.sortedMarkers.findIndex(
        (m: any) => m.pipelineSectionMarkerId === currentMarkerId
      );
    },
    async navigateToMarker(marker: PipelineSectionMarkerState) {
      this.activePipelineSectionMarkerId = marker.pipelineSectionMarkerId;
      this.selectedMarkerName = marker.name;
      this.currentMarkerIndex = this.sortedMarkers.findIndex(
        (m) => m.pipelineSectionMarkerId === marker.pipelineSectionMarkerId
      );
      this.passages = [];
      this.errorMsg = '';
      this.resetPassagePage();
      this.$emit('marker-changed', {
        pigRunId: this.activePigRunId ?? marker.pigRunId,
        pipelineSectionMarkerId: marker.pipelineSectionMarkerId,
        name: marker.name,
        lat: marker.latitude,
        lon: marker.longitude,
        trackState: marker.trackState,
        description: marker.description,
      } as SelectedMarker);
      await this.fetchPassages();
    },
    onMarkerFilterSelected(pipelineSectionMarkerId: number | null) {
      if (pipelineSectionMarkerId == null) return;
      const marker = this.sortedMarkers.find(
        (m) => m.pipelineSectionMarkerId === pipelineSectionMarkerId
      );
      if (marker) this.navigateToMarker(marker);
    },
    clearTimeFilter() {
      this.filterFrom = '';
      this.filterTo = '';
    },
    resetPassagePage() {
      this.passagePagination = { ...this.passagePagination, page: 1 } as any;
    },
    onPrevMarker() {
      const markers = this.sortedMarkers;
      if (markers.length < 2 || this.currentMarkerIndex < 0) return;
      const prevIndex =
        (this.currentMarkerIndex - 1 + markers.length) % markers.length;
      this.navigateToMarker(markers[prevIndex]);
    },
    onNextMarker() {
      const markers = this.sortedMarkers;
      if (markers.length < 2 || this.currentMarkerIndex < 0) return;
      const nextIndex = (this.currentMarkerIndex + 1) % markers.length;
      this.navigateToMarker(markers[nextIndex]);
    },
    async fetchPassages() {
      if (!this.activePipelineSectionMarkerId) return;
      this.loading = true;
      try {
        const response = await PigRunService.getMarkerPassages(
          this.activePipelineSectionMarkerId
        );
        this.passages = response.data;
      } catch (error) {
        console.error('Failed to fetch marker passages:', error);
        this.errorMsg = 'Failed to load passages';
      } finally {
        this.loading = false;
      }
    },
    closeViewPsgs() {
      this.passagesDialog = false;
    },
    onSignalRPassageOrRunUpdate() {
      if (!this.passagesDialog || !this.activePipelineSectionMarkerId) return;
      // Trailing-edge debounce: reset the short timer on every message so
      // we don't fetch on every single event during a burst.
      if (this.refreshDebounceTimer) {
        clearTimeout(this.refreshDebounceTimer);
      }
      this.refreshDebounceTimer = setTimeout(() => {
        this.flushRefresh();
      }, 1500);
      // Max-wait guarantee: if messages keep arriving without a gap,
      // force a fetch after 5 seconds so the dialog never goes stale.
      if (!this.refreshMaxWaitTimer) {
        this.refreshMaxWaitTimer = setTimeout(() => {
          this.flushRefresh();
        }, 5000);
      }
    },
    flushRefresh() {
      if (this.refreshDebounceTimer) {
        clearTimeout(this.refreshDebounceTimer);
        this.refreshDebounceTimer = null;
      }
      if (this.refreshMaxWaitTimer) {
        clearTimeout(this.refreshMaxWaitTimer);
        this.refreshMaxWaitTimer = null;
      }
      this.fetchPassages();
    },
    handlePassageVerified({ passage }: any) {
      const idx = this.passages.findIndex(
        (p: any) => p.passageId === passage.passageId
      );
      if (idx !== -1) {
        this.passages[idx] = {
          ...this.passages[idx],
          passageWaveforms: [...passage.passageWaveforms],
        };
      }
    },
    getMLWaveforms(row: IPassage): IPassageWaveformSummary[] {
      return getMLWaveforms(row);
    },
    getMLMean(prediction: boolean, confidence: number): number {
      return getMLMean(prediction, confidence);
    },
    getMLColor(prediction: boolean, confidence: number): string {
      return getMLColor(prediction, confidence);
    },
    getMLIcon(color: string): string {
      if (color.includes('red')) return 'block';
      if (color.includes('green')) return 'check_circle_outline';
      return 'help';
    },
    getMLConfidenceLabel(prediction: boolean, confidence: number): string {
      const mean = this.getMLMean(prediction, confidence);
      return ' ' + (50 * mean + 50).toFixed(1) + '%';
    },
    wfColor(wf: IPassageWaveformSummary): string {
      return waveformMLColor(wf);
    },
    wfLabel(wf: IPassageWaveformSummary): string {
      const type =
        WaveformTypeLabel[wf.passageWaveformType] || wf.passageWaveformType;
      const mean = this.getMLMean(
        wf.mlPrediction ?? false,
        wf.mlConfidence ?? 0
      );
      const pct = Math.round(50 * mean + 50) + '%';
      return `${type} ${pct}`;
    },
  },
});
</script>
